/**
 * Drawdowns List Tab
 *
 * Displays a filterable list of drawdowns with batch management capabilities
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, RefreshCw } from 'lucide-react';
import { DrawdownGroupedView } from './DrawdownGroupedView';
import type {
  DrawdownFilters,
  DrawdownGroup,
  DrawdownBatch,
} from '../../types/drawdown';
import type { FundSummary } from '../../types/fund';
import { DrawdownBatchService } from '@/services/drawdowns/drawdownBatchService';

interface DrawdownsListTabProps {
  drawdownGroups: DrawdownGroup[];
  totalCount: number;
  isLoading: boolean;
  fundId?: string;
  funds?: FundSummary[];
  currentPage: number;
  pageSize: number;
  filters: DrawdownFilters;
  onFilterChange: (
    key: keyof DrawdownFilters,
    value: string | undefined
  ) => void;
  onClearFilters: () => void;
  onCreateNew?: () => void;
  onRecalculate: (drawdownId: string) => void;
  onPreviewDrawdown: (drawdownId: string) => void;
  onPreviewBatch: (drawdownId: string, batchId: string) => void;
  onPageChange: (page: number) => void;
}

export function DrawdownsListTab({
  drawdownGroups,
  totalCount,
  isLoading,
  fundId,
  funds,
  currentPage,
  pageSize,
  filters,
  onFilterChange,
  onClearFilters,
  onCreateNew,
  onRecalculate,
  onPreviewDrawdown,
  onPreviewBatch,
  onPageChange,
}: DrawdownsListTabProps) {
  // State for managing batch loading
  const [batchCache, setBatchCache] = useState<Record<string, DrawdownBatch[]>>(
    {}
  );
  const [loadingBatches, setLoadingBatches] = useState<Set<string>>(
    () => new Set()
  );

  // Fetch batches for a specific drawdown when expanded
  const fetchBatchesForDrawdown = async (drawdownId: string) => {
    if (!fundId || batchCache[drawdownId] || loadingBatches.has(drawdownId)) {
      return; // Already loaded or loading
    }

    setLoadingBatches((prev) => new Set(prev).add(drawdownId));

    try {
      const batches = await DrawdownBatchService.getBatches(fundId, drawdownId);
      setBatchCache((prev) => ({ ...prev, [drawdownId]: batches }));
    } catch (err) {
      console.error('Failed to load batches:', err);
    } finally {
      setLoadingBatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(drawdownId);
        return newSet;
      });
    }
  };

  const hasActiveFilters =
    filters.status ||
    filters.payment_status ||
    filters.search ||
    filters.date_from ||
    filters.date_to ||
    filters.fund_id;

  // Merge cached batches into drawdown groups
  const drawdownGroupsWithBatches = drawdownGroups.map((group) => {
    const cachedBatches = batchCache[group.drawdownId];
    if (!cachedBatches) {
      return group; // No batches loaded yet
    }

    // Transform batches to view format and merge into group
    const batchViews = cachedBatches.map((batch) => ({
      batchId: batch.id,
      batchNumber: batch.batch_number,
      batchName: batch.batch_name,
      noticeDate: batch.actual_notice_date ?? batch.notice_date,
      dueDate: batch.due_date,
      investors: batch.investor_count,
      amount: batch.total_amount,
      status: batch.status,
      invoicesSent: batch.emails_sent,
      invoicesTotal: batch.emails_total,
      pdfGenerated:
        batch.pdfs_completed === batch.pdfs_total && batch.pdfs_total > 0,
      pdf_generation_status: batch.pdf_generation_status,
      pdfs_completed: batch.pdfs_completed,
      pdfs_failed: batch.pdfs_failed,
      pdfs_total: batch.pdfs_total,
    }));

    return {
      ...group,
      batches: batchViews,
    };
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            Showing {drawdownGroups.length} of {totalCount} drawdowns
          </div>

          <div className="flex items-center gap-2">
            {/* Only show fund filter if not in fund-scoped context */}
            {!fundId && funds && funds.length > 0 && (
              <Select
                value={filters.fund_id || 'all'}
                onValueChange={(value) =>
                  onFilterChange('fund_id', value === 'all' ? undefined : value)
                }
              >
                <SelectTrigger className="w-24 h-8">
                  <SelectValue placeholder="Fund" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Funds</SelectItem>
                  {funds.map((fund) => (
                    <SelectItem key={fund.id} value={fund.id}>
                      {fund.display_name || fund.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={filters.status || 'all'}
              onValueChange={(value) =>
                onFilterChange('status', value === 'all' ? undefined : value)
              }
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="completed">Complete</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.payment_status || 'all'}
              onValueChange={(value) =>
                onFilterChange(
                  'payment_status',
                  value === 'all' ? undefined : value
                )
              }
            >
              <SelectTrigger className="w-32 h-8">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partially Paid</SelectItem>
                <SelectItem value="paid">Fully Paid</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder="Search reference or purpose..."
              value={filters.search || ''}
              onChange={(e) =>
                onFilterChange('search', e.target.value || undefined)
              }
              className="w-48 h-8"
            />

            <Input
              type="date"
              placeholder="From"
              value={filters.date_from || ''}
              onChange={(e) =>
                onFilterChange('date_from', e.target.value || undefined)
              }
              className="w-40 h-8"
            />

            <Input
              type="date"
              placeholder="To"
              value={filters.date_to || ''}
              onChange={(e) =>
                onFilterChange('date_to', e.target.value || undefined)
              }
              className="w-40 h-8"
            />

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-8 px-2 text-xs"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading capital calls...
          </div>
        )}
        {!isLoading && drawdownGroups.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="mb-4">
              No drawdowns found. Create your first drawdown to get started.
            </p>
            {onCreateNew && (
              <Button onClick={onCreateNew}>
                <Plus className="mr-2 h-4 w-4" />
                Create Drawdown
              </Button>
            )}
          </div>
        )}
        {!isLoading && drawdownGroups.length > 0 && (
          <DrawdownGroupedView
            drawdowns={drawdownGroupsWithBatches}
            fundId={fundId || ''}
            onRecalculate={onRecalculate}
            onPreviewDrawdown={onPreviewDrawdown}
            onPreviewBatch={onPreviewBatch}
            onExpandDrawdown={fetchBatchesForDrawdown}
            loadingBatches={loadingBatches}
          />
        )}

        {/* Pagination */}
        {totalCount > pageSize && (
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage * pageSize >= totalCount}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
